import axios from 'axios';
import { action, computed, IReactionDisposer, observable, reaction, runInAction } from 'mobx';
import { debounce } from 'throttle-debounce';
import Config from '../Config';
import { PimMenuItem, Product } from '../content/ProductInterfaces';
import ErrorUtil from '../error/ErrorUtil';
import {
  AllAndArchivedFilterOrder,
  DealerNewsFilterOrder,
  DealerNewsSortOrder,
  DealerNewsTopicsResultCounts,
  NewsSortOrder,
  ProductsSortOrder,
  SearchEvent,
  SearchResultBase,
  SearchSuggestion,
  SearchSuggestionResults,
  SearchSuggestionWrapper,
  ShopSearchSuggestion,
  SuggestionGroup,
  WebContentFilterOrder,
  WebContentResults,
} from '../search/SearchInterfaces';
import { FetchStatus } from '../util/FetchStatus';
import { cloudinaryUrlWithSize, createParamsString } from '../util/PathUtil';
import WebSearchApi from '../util/WebSearchApiUtil';
import LanguageStore from './LanguageStore';
import ProductPriceStore from './ProductPriceStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import SegmentStore, { SEGMENT_PRIVATE } from './SegmentStore';
import SsoStore, { CUSTOMER_TYPE_BUSINESS, CUSTOMER_TYPE_PRIVATE } from './SsoStore';

const ITEMS_PER_PAGE: number = 12;
const INITIAL_ITEMS_PER_PAGE_FOR_DOCUMENTS: number = 4;
const INITIAL_ITEMS_PER_PAGE_FOR_SERVICES: number = 8;
const ESHOP_TAB_VALUE = 'pood';
const ONLINE_HELP_TAB_VALUE = 'juhendid';

export const CATEGORY_ARCHIVED = 'Archived';
export const EMPLOYEE_CATEGORY = 'dealer';
export const EMPLOYEE_CATEGORY_ARCHIVED = EMPLOYEE_CATEGORY + CATEGORY_ARCHIVED;
export const CONTENT_CATEGORY = 'content';
export const DOCUMENT_CATEGORY = 'documents';
export const SERVICE_CATEGORY = 'services';

export const ESHOP_CATEGORY = 'shop';
export const ESHOP_CATEGORY_ARCHIVED = ESHOP_CATEGORY + CATEGORY_ARCHIVED;
export const ONLINE_HELP_CATEGORY = 'onlineHelp';
export const SELFSERVICE_CATEGORY = 'selfService';
export const NEWS_CATEGORY = 'news';
export const NEWS_CATEGORY_ARCHIVED = NEWS_CATEGORY + CATEGORY_ARCHIVED;

const CATEGORIES_IN_ORDER = [EMPLOYEE_CATEGORY, NEWS_CATEGORY, CONTENT_CATEGORY, ESHOP_CATEGORY, ONLINE_HELP_CATEGORY, SELFSERVICE_CATEGORY];
const ARCHIVED_CATEGORIES = [EMPLOYEE_CATEGORY_ARCHIVED, NEWS_CATEGORY_ARCHIVED, ESHOP_CATEGORY_ARCHIVED];

const BASE_WRAPPER_VALUES: {
  results: [];
  total: number;
  status: FetchStatus;
  currentPage: number;
  perPage: number;
} = {
  results: [],
  status: FetchStatus.UNINITIALIZED,
  total: 0,
  currentPage: 0,
  perPage: 0,
};

class SearchStore {
  @observable accessor searchWrappers: {
    [key: string]: SearchResultBase<any>;
  } = {
    documents: {
      ...BASE_WRAPPER_VALUES,
      filterOrder: WebContentFilterOrder.ALL,
      segmentedResults: [],
      segmentedPage: 0,
    },
    services: {
      ...BASE_WRAPPER_VALUES,
      filterOrder: WebContentFilterOrder.ALL,
      segmentedResults: [],
      segmentedPage: 0,
    },
    shop: {
      ...BASE_WRAPPER_VALUES,
      filterOrder: AllAndArchivedFilterOrder.ALL,
    },
    shopArchived: { ...BASE_WRAPPER_VALUES },
    onlineHelp: { ...BASE_WRAPPER_VALUES },
    selfService: { ...BASE_WRAPPER_VALUES },
    dealer: {
      ...BASE_WRAPPER_VALUES,
      filterOrder: DealerNewsFilterOrder.ALL,
      sortOrder: DealerNewsSortOrder.WEIGHT,
      segmentedResults: [],
      segmentedPage: 0,
    },
    dealerArchived: { ...BASE_WRAPPER_VALUES },
    news: {
      ...BASE_WRAPPER_VALUES,
      filterOrder: AllAndArchivedFilterOrder.ALL,
      sortOrder: NewsSortOrder.WEIGHT,
    },
    newsArchived: { ...BASE_WRAPPER_VALUES, sortOrder: NewsSortOrder.WEIGHT },
  };
  @observable accessor suggestionsWrapper: SearchSuggestionWrapper = {
    ...BASE_WRAPPER_VALUES,
  };
  @observable
  accessor dealerNewsTopicsResultCounts: DealerNewsTopicsResultCounts = {
    newsCount: undefined,
    instructionsCount: undefined,
    campaignsCount: undefined,
    technicianCount: undefined,
  };
  @observable accessor dealerSearchSuggestionsWrapper: SearchSuggestionWrapper = {
    ...BASE_WRAPPER_VALUES,
  };

  @observable accessor query: string = '';
  @observable accessor lastSearchedQuery: string = '';
  @observable accessor selectedTabs: string[] = [];
  @observable accessor onlyShopSearch: boolean = false;
  @observable accessor allContentResults: WebContentResults = {
    total: 0,
    privateTotal: 0,
    businessTotal: 0,
    status: FetchStatus.UNINITIALIZED,
    filterOrder: WebContentFilterOrder.ALL,
  };
  @observable accessor subPageCategories: PimMenuItem[] = [];
  public initialTab: string;
  public debouncedFetchSearchSuggestions: debounce<(query: string, onlyShopSuggestions: boolean) => Promise<void>>;

  private disposers: IReactionDisposer[] = [];
  private suggestionCancel = axios.CancelToken.source();

  private languageStore: LanguageStore;
  private ssoStore: SsoStore;
  private routeStore: RouteStore;
  private segmentStore: SegmentStore;
  private productPriceStore: ProductPriceStore;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    this.ssoStore = rootStore.ssoStore;
    this.routeStore = rootStore.routeStore;
    this.segmentStore = rootStore.segmentStore;
    this.productPriceStore = rootStore.productPriceStore;
    this.debouncedFetchSearchSuggestions = debounce(300, this.fetchSuggestions);

    this.disposers.push(
      reaction(
        () => this.ssoStore.customerId,
        () => {
          this.searchWrappers[ESHOP_CATEGORY].total > 0 &&
            this.productPriceStore.fetchPrices(
              this.searchWrappers[ESHOP_CATEGORY].results.map((p: Product) => p.productCode),
              this.searchWrappers[ESHOP_CATEGORY].results,
            );
          this.changeWebContentFilterForCustomer();
        },
      ),
      reaction(
        () => this.allContentResults.total,
        () => this.changeWebContentFilterForCustomer(),
      ),
    );
  }

  private changeWebContentFilterForCustomer = () => {
    const customerType = this.ssoStore.customer?.type;
    const filterOrder =
      customerType === 'B2B' ? WebContentFilterOrder.BUSINESS : customerType === 'B2C' ? WebContentFilterOrder.PRIVATE : WebContentFilterOrder.ALL;
    this.allContentResults.total && this.changeWebContentFilters(filterOrder);
  };

  public addToSearchResults = (isArchived = false, category?: string) => {
    if (category === DOCUMENT_CATEGORY) {
      return this.fetchDocumentsOrServices(this.query, true, FetchStatus.FETCHING_MORE);
    }
    if (category === SERVICE_CATEGORY) {
      return this.fetchDocumentsOrServices(this.query, false, FetchStatus.FETCHING_MORE);
    }
    if (this.selectedTabs.includes(EMPLOYEE_CATEGORY)) {
      return this.switchDealerNewsPage(isArchived);
    }
    if (this.selectedTabs.includes(ESHOP_CATEGORY)) {
      return this.fetchMoreEShopResults(isArchived);
    }
    if (this.selectedTabs.includes(ONLINE_HELP_CATEGORY)) {
      return this.fetchMoreOnlineHelpResults();
    }
    if (this.selectedTabs.includes(SELFSERVICE_CATEGORY)) {
      return this.fetchMoreSelfServiceResults();
    }
    if (this.selectedTabs.includes(NEWS_CATEGORY)) {
      return this.fetchMoreNewsResults(isArchived);
    }
  };

  @action
  public resetArchives() {
    this.resetDealerArchive();
    this.resetShopArchive();
    this.resetNewsArchive();
  }

  private resetSuggestionRequestCancelToken() {
    this.suggestionCancel = axios.CancelToken.source();
  }

  @action
  private resetDealerArchive() {
    this.searchWrappers[EMPLOYEE_CATEGORY_ARCHIVED] = BASE_WRAPPER_VALUES;
  }

  @action
  private resetShopArchive() {
    this.searchWrappers[ESHOP_CATEGORY_ARCHIVED] = BASE_WRAPPER_VALUES;
  }

  @action
  private resetNewsArchive() {
    this.searchWrappers[NEWS_CATEGORY_ARCHIVED] = BASE_WRAPPER_VALUES;
  }

  public fetchContentResultsCount = async (status: FetchStatus = FetchStatus.FETCHING) => {
    runInAction(() => {
      this.allContentResults.status = status;
    });

    try {
      await this.getSegmentedContentResultNumber();
      await this.getSegmentedContentResultNumber(WebContentFilterOrder.PRIVATE);
      await this.getSegmentedContentResultNumber(WebContentFilterOrder.BUSINESS);
      runInAction(() => {
        this.allContentResults.status = FetchStatus.FETCHED;
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      runInAction(() => {
        this.allContentResults.total = 0;
        this.allContentResults.privateTotal = 0;
        this.allContentResults.businessTotal = 0;
        this.allContentResults.status = FetchStatus.ERROR;
      });
    }
  };

  public fetchDocumentsOrServices = async (search: string, searchDocuments: boolean, status: FetchStatus = FetchStatus.FETCHING, newQuery?: boolean) => {
    const category = searchDocuments ? DOCUMENT_CATEGORY : SERVICE_CATEGORY;
    const wrapper = this.searchWrappers[category];
    const initialPerPage = searchDocuments ? INITIAL_ITEMS_PER_PAGE_FOR_DOCUMENTS : INITIAL_ITEMS_PER_PAGE_FOR_SERVICES;
    runInAction(() => {
      wrapper.status = status;
    });
    if (status === FetchStatus.FETCHING) {
      (wrapper.filterOrder === WebContentFilterOrder.ALL || newQuery) && this.resetSearchWrapper(category);
    } else {
      runInAction(() => {
        wrapper.filterOrder === WebContentFilterOrder.ALL ? (wrapper.currentPage += 1) : (wrapper.segmentedPage += 1);
      });
    }
    try {
      const response = await WebSearchApi.get('/web/documents-or-services', {
        params: {
          page: wrapper.filterOrder === WebContentFilterOrder.ALL ? wrapper.currentPage : wrapper.segmentedPage,
          query: search,
          perPage: status === FetchStatus.FETCHING ? initialPerPage : ITEMS_PER_PAGE,
          lang: this.languageStore.currentLanguage,
          segment: wrapper.filterOrder === WebContentFilterOrder.ALL || newQuery ? '' : wrapper.filterOrder,
          searchDocuments: searchDocuments,
        },
      });
      this.setWrapperResults(category, response.data, wrapper.filterOrder !== WebContentFilterOrder.ALL);
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      runInAction(() => {
        wrapper.status = wrapper.currentPage === 0 ? FetchStatus.ERROR : FetchStatus.FETCH_MORE_ERROR;
      });
    }
  };

  private getSegmentedContentResultNumber = async (segment?: WebContentFilterOrder) => {
    const response = await WebSearchApi.get('/web/search', {
      params: {
        page: 0,
        query: this.query,
        lang: this.languageStore.currentLanguage,
        segment: segment ? (segment === WebContentFilterOrder.PRIVATE ? 'era' : 'ari') : undefined,
      },
    });
    this.setContentTotalForSegment(response.data.total, segment);
  };

  public changeWebContentFilters = (newFilter: WebContentFilterOrder) => {
    this.changeWebContentFilter(newFilter, DOCUMENT_CATEGORY);
    this.changeWebContentFilter(newFilter, SERVICE_CATEGORY);
  };

  public changeWebContentFilter = (newFilter: WebContentFilterOrder, category: string) => {
    const wrapper = this.searchWrappers[category];
    if (newFilter === WebContentFilterOrder.ALL) {
      runInAction(() => {
        wrapper.filterOrder = newFilter;
      });
      if (category === DOCUMENT_CATEGORY) {
        runInAction(() => {
          this.allContentResults.filterOrder = newFilter;
        });
      }
    } else {
      runInAction(() => {
        wrapper.segmentedResults = [];
        wrapper.segmentedPage = 0;
        wrapper.segmentedTotal = 0;
        wrapper.filterOrder = newFilter;
        if (category === DOCUMENT_CATEGORY) {
          this.allContentResults.filterOrder = newFilter;
          this.fetchDocumentsOrServices(this.query, true, FetchStatus.FETCHING, false);
        } else if (category === SERVICE_CATEGORY) {
          this.fetchDocumentsOrServices(this.query, false, FetchStatus.FETCHING, false);
        }
      });
    }
  };

  public changeShopFilterOrder = (filter: AllAndArchivedFilterOrder) => {
    runInAction(() => {
      this.searchWrappers[ESHOP_CATEGORY].filterOrder = filter;
    });
  };

  public changeNewsFilterOrder = (filter: AllAndArchivedFilterOrder) => {
    runInAction(() => {
      this.searchWrappers[NEWS_CATEGORY].filterOrder = filter;
    });
  };

  public changeDealerNewsFilterOrder = async (filter: DealerNewsFilterOrder) => {
    runInAction(() => {
      const wrapper = this.searchWrappers[EMPLOYEE_CATEGORY];
      if ([DealerNewsFilterOrder.ALL, DealerNewsFilterOrder.ARCHIVED].includes(filter)) {
        wrapper.filterOrder = filter;
      } else {
        wrapper.segmentedResults = [];
        wrapper.segmentedPage = 0;
        wrapper.segmentedTotal = 0;
        wrapper.filterOrder = filter;
        this.fetchDealerSearchResults(this.query, FetchStatus.FETCHING);
      }
    });
  };

  @action
  public setSubPageCategory(menuItem: PimMenuItem) {
    if (!this.subPageCategories.some((item) => item.url === menuItem.url)) {
      this.subPageCategories.push(menuItem);
    }
  }

  @action
  public resetSubPageCategories() {
    this.subPageCategories = [];
  }

  public setWrapperResults = (category: string, result: SearchResultBase<any>, segmented?: boolean) => {
    runInAction(() => {
      const wrapper = this.searchWrappers[category];
      if (!segmented) {
        wrapper.results = wrapper.results.concat(result.results || result.products);
        wrapper.total = result.total;
      } else {
        wrapper.segmentedResults = wrapper.segmentedResults.concat(result.results || result.products);
        wrapper.segmentedTotal = result.total;
      }
      wrapper.queryId = result.queryId;
      wrapper.status = FetchStatus.FETCHED;
    });
  };

  private resetSearchWrapper = (category: string) => {
    runInAction(() => {
      this.searchWrappers[category].total = 0;
      this.searchWrappers[category].results = [];
      this.searchWrappers[category].currentPage = 0;
      if ([DOCUMENT_CATEGORY, SERVICE_CATEGORY].includes(category)) {
        this.searchWrappers[category].filterOrder = WebContentFilterOrder.ALL;
      }
    });
  };

  public resetSearchWrappersAndQuery = () => {
    runInAction(() => {
      [
        EMPLOYEE_CATEGORY,
        EMPLOYEE_CATEGORY_ARCHIVED,
        ESHOP_CATEGORY,
        ESHOP_CATEGORY_ARCHIVED,
        ONLINE_HELP_CATEGORY,
        SELFSERVICE_CATEGORY,
        NEWS_CATEGORY,
        NEWS_CATEGORY_ARCHIVED,
        DOCUMENT_CATEGORY,
        SERVICE_CATEGORY,
      ].forEach((category) => this.resetSearchWrapper(category));
      this.resetAllContentResults();
      this.query = '';
    });
  };

  private resetAllContentResults = () => {
    runInAction(() => {
      this.allContentResults = {
        status: FetchStatus.UNINITIALIZED,
        total: 0,
        businessTotal: 0,
        filterOrder: WebContentFilterOrder.ALL,
        privateTotal: 0,
      };
    });
  };

  private setContentTotalForSegment(totalContent: number, segment?: WebContentFilterOrder) {
    if (!segment) {
      runInAction(() => {
        this.allContentResults.total = totalContent;
      });
    }
    runInAction(() => {
      segment === WebContentFilterOrder.PRIVATE ? (this.allContentResults.privateTotal = totalContent) : (this.allContentResults.businessTotal = totalContent);
    });
  }

  @computed
  public get isAnyCategoryFetching() {
    return (
      this.allContentResults.status === FetchStatus.FETCHING ||
      Object.entries(this.searchWrappers)
        .filter(([key]) => !ARCHIVED_CATEGORIES.includes(key))
        .some(([_key, wrapper]) => wrapper.status === FetchStatus.FETCHING && (!wrapper.filterOrder || wrapper.filterOrder === WebContentFilterOrder.ALL))
    );
  }

  public isDebouncing = (query: string): boolean => {
    return this.lastSearchedQuery !== query && query?.length > 2;
  };

  @computed
  private get shouldSelectEmployeeTab() {
    return this.ssoStore.isDealer && (this.searchWrappers[EMPLOYEE_CATEGORY].total || this.searchWrappers[EMPLOYEE_CATEGORY_ARCHIVED].total);
  }

  public fetchInitialTab = async (query: string) => {
    runInAction(() => {
      this.initialTab = '';
    });
    if (query.length < 2) {
      return Promise.resolve();
    }
    try {
      const initialTabResponse = await WebSearchApi.get('/common/initial-tab', {
        params: { query },
      });
      if (initialTabResponse.data) {
        runInAction(() => {
          this.initialTab = initialTabResponse.data;
        });
      }
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.initialTab = '';
      });
    }
  };

  public setInitialTab = () => {
    if (this.isOnlyShopSearch) {
      this.setInitialCategory(ESHOP_CATEGORY);
      return;
    }
    if (this.routeStore.decodedParams.tab === ONLINE_HELP_TAB_VALUE && this.searchWrappers[ONLINE_HELP_CATEGORY].total) {
      this.setInitialCategory(ONLINE_HELP_CATEGORY);
    } else if (this.routeStore.decodedParams.tab === ESHOP_TAB_VALUE && this.searchWrappers[ESHOP_CATEGORY].total) {
      this.setInitialCategory(ESHOP_CATEGORY);
    } else if (this.shouldSelectEmployeeTab) {
      this.setInitialCategory(EMPLOYEE_CATEGORY);
    } else if (!!this.initialTab && !!this.getSearchResultCount(this.initialTab)) {
      this.changeCategories([this.initialTab]);
    } else if (this.searchWrappers[SELFSERVICE_CATEGORY].total === 1) {
      this.setInitialCategory(SELFSERVICE_CATEGORY);
    } else {
      this.setInitialCategory(this.findCategoryWithMostResults());
    }
  };

  private findCategoryWithMostResults = (): string => {
    let maxCategory: string = this.searchCategories && this.searchCategories[0];
    const searchCategoriesWithoutNews = this.searchCategories.filter((c) => c !== NEWS_CATEGORY);
    searchCategoriesWithoutNews.length &&
      searchCategoriesWithoutNews.forEach((category) => {
        if (maxCategory === NEWS_CATEGORY) {
          runInAction(() => {
            maxCategory = category;
          });
        }
        if (this.getSearchResultCount(category) > this.getSearchResultCount(maxCategory)) {
          runInAction(() => {
            maxCategory = category;
          });
        }
      });
    return maxCategory || '';
  };

  public getSearchResultCount = (category: string, showArchived?: boolean): number => {
    if (category === EMPLOYEE_CATEGORY) {
      return showArchived && this.searchWrappers[EMPLOYEE_CATEGORY].filterOrder === DealerNewsFilterOrder.ARCHIVED
        ? this.searchWrappers[EMPLOYEE_CATEGORY_ARCHIVED].total
        : this.searchWrappers[EMPLOYEE_CATEGORY].total;
    }
    if (category === ESHOP_CATEGORY) {
      return showArchived && this.searchWrappers[ESHOP_CATEGORY].filterOrder === AllAndArchivedFilterOrder.ARCHIVED
        ? this.searchWrappers[ESHOP_CATEGORY_ARCHIVED].total
        : this.searchWrappers[ESHOP_CATEGORY].total;
    }
    if (category === NEWS_CATEGORY) {
      return showArchived && this.searchWrappers[NEWS_CATEGORY].filterOrder === AllAndArchivedFilterOrder.ARCHIVED
        ? this.searchWrappers[NEWS_CATEGORY_ARCHIVED].total
        : this.searchWrappers[NEWS_CATEGORY].total;
    }
    if (category === CONTENT_CATEGORY) {
      return this.allContentResults.total;
    }
    return this.searchWrappers[category].total;
  };

  public getDealerNewsTopicResultCount = (topic: string): number => {
    switch (topic) {
      case DealerNewsFilterOrder.NEWS:
        return this.dealerNewsTopicsResultCounts.newsCount;
      case DealerNewsFilterOrder.TECHNICIAN:
        return this.dealerNewsTopicsResultCounts.technicianCount;
      case DealerNewsFilterOrder.INSTRUCTIONS:
        return this.dealerNewsTopicsResultCounts.instructionsCount;
      case DealerNewsFilterOrder.CAMPAIGNS:
        return this.dealerNewsTopicsResultCounts.campaignsCount;
      default:
        return this.searchWrappers[EMPLOYEE_CATEGORY].total;
    }
  };

  public setDealerNewsTopicResultCount = (topic: string, count: number) => {
    runInAction(() => {
      switch (topic) {
        case DealerNewsFilterOrder.NEWS:
          this.dealerNewsTopicsResultCounts.newsCount = count;
          break;
        case DealerNewsFilterOrder.TECHNICIAN:
          this.dealerNewsTopicsResultCounts.technicianCount = count;
          break;
        case DealerNewsFilterOrder.INSTRUCTIONS:
          this.dealerNewsTopicsResultCounts.instructionsCount = count;
          break;
        case DealerNewsFilterOrder.CAMPAIGNS:
          this.dealerNewsTopicsResultCounts.campaignsCount = count;
          break;
        default:
          return;
      }
    });
  };

  public setInitialCategory = (category: string) => {
    runInAction(() => {
      this.initialTab = category;
      this.changeCategories([category]);
    });
  };

  public setInitialArchivedFilter = () => {
    runInAction(() => {
      this.changeNewsFilterOrder(AllAndArchivedFilterOrder.ALL);
      this.changeShopFilterOrder(AllAndArchivedFilterOrder.ALL);
      this.changeDealerNewsFilterOrder(DealerNewsFilterOrder.ALL);
      if (!this.searchWrappers[NEWS_CATEGORY].total && this.searchWrappers[NEWS_CATEGORY_ARCHIVED].total) {
        this.changeNewsFilterOrder(AllAndArchivedFilterOrder.ARCHIVED);
      }
      if (!this.searchWrappers[ESHOP_CATEGORY].total && this.searchWrappers[ESHOP_CATEGORY_ARCHIVED].total) {
        this.changeShopFilterOrder(AllAndArchivedFilterOrder.ARCHIVED);
      }
      if (!this.searchWrappers[EMPLOYEE_CATEGORY].total && this.searchWrappers[EMPLOYEE_CATEGORY_ARCHIVED].total) {
        this.changeDealerNewsFilterOrder(DealerNewsFilterOrder.ARCHIVED);
      }
    });
  };

  public postSearchTabAnalytics = async (tabName: string) => {
    const data = {
      initialTab: this.initialTab,
      selectedTab: tabName,
      query: this.query,
    };
    try {
      WebSearchApi.post('/common/analytics', data);
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
    }
  };

  public changeCategories = (categories: string[] = null) => {
    if (this.selectedTabs !== categories) {
      runInAction(() => {
        this.selectedTabs = categories;
      });
    }
  };

  public setIsOnlyShopSearch = (onlyShopSearch: boolean) => {
    runInAction(() => {
      this.onlyShopSearch = onlyShopSearch;
    });
  };

  @computed
  public get isOnlyShopSearch(): boolean {
    return this.onlyShopSearch || this.routeStore.decodedParams.onlyShopSearch === 'true';
  }

  public numberOfMoreResults = (category: string): number => {
    if (category === DOCUMENT_CATEGORY && this.searchWrappers[DOCUMENT_CATEGORY].filterOrder !== WebContentFilterOrder.ALL) {
      return this.searchWrappers[DOCUMENT_CATEGORY].segmentedTotal - this.searchWrappers[DOCUMENT_CATEGORY].segmentedResults?.length;
    }
    if (category === SERVICE_CATEGORY && this.searchWrappers[SERVICE_CATEGORY].filterOrder !== WebContentFilterOrder.ALL) {
      return this.searchWrappers[SERVICE_CATEGORY].segmentedTotal - this.searchWrappers[SERVICE_CATEGORY].segmentedResults?.length;
    }
    if (
      category === EMPLOYEE_CATEGORY &&
      ![DealerNewsFilterOrder.ALL, DealerNewsFilterOrder.ARCHIVED].includes(this.searchWrappers[EMPLOYEE_CATEGORY].filterOrder)
    ) {
      return this.searchWrappers[EMPLOYEE_CATEGORY].segmentedTotal - this.searchWrappers[EMPLOYEE_CATEGORY].segmentedResults?.length;
    }
    return this.searchWrappers[category].total - this.searchWrappers[category].results?.length;
  };

  @computed
  get searchCategories(): string[] {
    const categories = Object.entries(this.searchWrappers).map(([key, val]) => {
      if (val.total && ![EMPLOYEE_CATEGORY_ARCHIVED, EMPLOYEE_CATEGORY].includes(key)) {
        return key;
      }
      if (
        key === EMPLOYEE_CATEGORY &&
        this.ssoStore.isDealer &&
        (this.getSearchResultCount(EMPLOYEE_CATEGORY) || this.getSearchResultCount(EMPLOYEE_CATEGORY_ARCHIVED) || val.status === 'ERROR')
      ) {
        return key;
      }
    });
    if (this.allContentResults.total) {
      categories.push(CONTENT_CATEGORY);
    }
    if (categories.includes(ESHOP_CATEGORY_ARCHIVED) && !categories.includes(ESHOP_CATEGORY)) {
      categories.push(ESHOP_CATEGORY);
    }
    if (categories.includes(NEWS_CATEGORY_ARCHIVED) && !categories.includes(NEWS_CATEGORY)) {
      categories.push(NEWS_CATEGORY);
    }
    return CATEGORIES_IN_ORDER.filter((category) => categories.includes(category));
  }

  public async fetchDealerSearchResults(search: string, status: FetchStatus = FetchStatus.FETCHING, archived: boolean = false) {
    const category = archived ? EMPLOYEE_CATEGORY_ARCHIVED : EMPLOYEE_CATEGORY;
    const wrapper = this.searchWrappers[category];
    runInAction(() => {
      wrapper.status = status;
    });
    const isFetchingMore = status === FetchStatus.FETCHING_MORE;
    const filterTopics = [DealerNewsFilterOrder.NEWS, DealerNewsFilterOrder.CAMPAIGNS, DealerNewsFilterOrder.TECHNICIAN, DealerNewsFilterOrder.INSTRUCTIONS];
    const isFilteredResults = filterTopics.includes(wrapper.filterOrder);
    if (!isFetchingMore) {
      !isFilteredResults && this.resetSearchWrapper(category);
    } else if (isFilteredResults) {
      runInAction(() => {
        wrapper.segmentedPage += 1;
      });
    }
    const page = isFilteredResults ? wrapper.segmentedPage : wrapper.currentPage;

    try {
      const response = await WebSearchApi.get('/dealer/search', {
        params: {
          query: search,
          perPage: isFetchingMore ? ITEMS_PER_PAGE : ITEMS_PER_PAGE * 2,
          page: isFetchingMore ? page + 1 : page,
          archived,
          sortBy: this.searchWrappers[EMPLOYEE_CATEGORY].sortOrder,
          topic: isFilteredResults && !archived ? wrapper.filterOrder : null,
        },
        withCredentials: true,
      });
      runInAction(() => {
        if (!archived && wrapper.total === 0 && !isFilteredResults) {
          filterTopics.forEach((topic) => this.fetchDealerNewsTopicCount(topic));
        }
        this.setWrapperResults(category, response.data, isFilteredResults);
        if (page === 0 && !archived) {
          this.fetchDealerSearchSuggestions();
        }
      });
    } catch (e) {
      runInAction(() => {
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
        wrapper.status = wrapper.currentPage === 0 ? FetchStatus.ERROR : FetchStatus.FETCH_MORE_ERROR;
      });
    }
  }

  public async fetchDealerNewsTopicCount(topic: string) {
    try {
      const response = await WebSearchApi.get('/dealer/search', {
        params: {
          query: this.query,
          perPage: 0,
          page: 1,
          archived: false,
          sortBy: this.searchWrappers[EMPLOYEE_CATEGORY].sortOrder,
          topic: topic,
        },
        withCredentials: true,
      });
      this.setDealerNewsTopicResultCount(topic, response.data.total);
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  }

  public async fetchEShopSearchResults(search: string, archived: boolean) {
    this.resetSubPageCategories();
    const wrapper = this.searchWrappers[archived ? ESHOP_CATEGORY_ARCHIVED : ESHOP_CATEGORY];
    runInAction(() => {
      wrapper.currentPage = 0;
      wrapper.results = [];
      if (search?.length <= 1) {
        wrapper.status = FetchStatus.FETCHED;
        wrapper.total = 0;
        return;
      }
      wrapper.status = FetchStatus.FETCHING;
    });
    await this.fetchEShopResults(search, archived);
  }

  public async refetchEShopSearchResults() {
    this.setResultsToRefetching(ESHOP_CATEGORY);
    await this.fetchEShopResults(this.query, false);
    this.setResultsToRefetching(ESHOP_CATEGORY_ARCHIVED);
    this.fetchEShopResults(this.query, true);
  }

  @action
  private setResultsToRefetching(category: string) {
    this.searchWrappers[category].status = FetchStatus.REFETCHING;
    this.searchWrappers[category].results = [];
    this.searchWrappers[category].currentPage = 0;
  }

  public async fetchMoreEShopResults(archived: boolean) {
    const wrapper = this.searchWrappers[archived ? ESHOP_CATEGORY_ARCHIVED : ESHOP_CATEGORY];
    runInAction(() => {
      wrapper.currentPage += 1;
      wrapper.status = FetchStatus.FETCHING_MORE;
    });
    await this.fetchEShopResults(this.query, archived);
  }

  private async fetchEShopResults(search: string, archived: boolean) {
    const category = archived ? ESHOP_CATEGORY_ARCHIVED : ESHOP_CATEGORY;
    const wrapper = this.searchWrappers[category];
    const { currentPage } = wrapper;
    const sortOrder = this.getEShopProductSortOrder().toLowerCase();
    const clientType = this.getClientType();

    if (this.languageStore.currentLanguage === 'en') {
      runInAction(() => {
        wrapper.status = FetchStatus.FETCHED;
      });
      return;
    }

    try {
      const { data } = await WebSearchApi.get('/shop/search', {
        params: {
          query: search,
          page: currentPage,
          sortBy: sortOrder,
          perPage: ITEMS_PER_PAGE,
          clientType,
          archived,
        },
        withCredentials: true,
      });
      this.setWrapperResults(category, data);
      data.results?.map((result: Product) => {
        if (result.menuItem) {
          this.setSubPageCategory(result.menuItem);
        }
      });

      this.productPriceStore.fetchPrices(
        (data.results || data.products).map((p: Product) => p.productCode),
        this.searchWrappers[category].results,
      );
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      runInAction(() => {
        wrapper.status = currentPage > 0 ? FetchStatus.FETCH_MORE_ERROR : FetchStatus.ERROR;
      });
    }
  }

  public async fetchOnlineHelpResults(search: string, status: FetchStatus = FetchStatus.FETCHING) {
    runInAction(() => {
      this.searchWrappers[ONLINE_HELP_CATEGORY].status = status;
    });
    if (status === FetchStatus.FETCHING) {
      this.resetSearchWrapper(ONLINE_HELP_CATEGORY);
    }
    if (!search?.length) {
      runInAction(() => {
        this.searchWrappers[ONLINE_HELP_CATEGORY].status = FetchStatus.FETCHED;
      });
      return;
    }
    try {
      const result = (
        await WebSearchApi.get('/oa/search', {
          params: {
            query: search,
            lang: this.languageStore.currentLanguage,
            page: this.searchWrappers[ONLINE_HELP_CATEGORY].currentPage,
            perPage: ITEMS_PER_PAGE,
          },
        })
      ).data;
      this.setWrapperResults(ONLINE_HELP_CATEGORY, result);
    } catch (e) {
      runInAction(() => {
        this.searchWrappers[ONLINE_HELP_CATEGORY].status = FetchStatus.ERROR;
      });
      this.resetSearchWrapper(ONLINE_HELP_CATEGORY);
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  }

  public async fetchMoreOnlineHelpResults() {
    runInAction(() => {
      this.searchWrappers[ONLINE_HELP_CATEGORY].currentPage += 1;
    });
    return this.fetchOnlineHelpResults(this.query, FetchStatus.FETCHING_MORE);
  }

  public async fetchSelfServiceResults(search: string, currentLanguage: string, status: FetchStatus = FetchStatus.FETCHING) {
    const wrapper = this.searchWrappers[SELFSERVICE_CATEGORY];
    runInAction(() => {
      wrapper.status = status;
    });
    if (status === 'FETCHING') {
      this.resetSearchWrapper(SELFSERVICE_CATEGORY);
    }
    if (!search?.length) {
      runInAction(() => {
        this.searchWrappers[SELFSERVICE_CATEGORY].status = FetchStatus.FETCHED;
      });
      return;
    }

    try {
      const result = (
        await WebSearchApi.get('/selfservice/search', {
          params: {
            query: search,
            lang: currentLanguage,
            page: this.searchWrappers[SELFSERVICE_CATEGORY].currentPage,
            perPage: ITEMS_PER_PAGE,
          },
        })
      ).data;
      this.setWrapperResults(SELFSERVICE_CATEGORY, result);
    } catch (e) {
      runInAction(() => {
        this.searchWrappers[SELFSERVICE_CATEGORY].status = FetchStatus.ERROR;
        this.resetSearchWrapper(SELFSERVICE_CATEGORY);
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      });
    }
  }

  public async fetchMoreSelfServiceResults() {
    runInAction(() => {
      this.searchWrappers[SELFSERVICE_CATEGORY].currentPage += 1;
    });
    return this.fetchSelfServiceResults(this.query, this.languageStore.currentLanguage, FetchStatus.FETCHING_MORE);
  }

  public async fetchNewsResults(search: string, status: FetchStatus = FetchStatus.FETCHING, archived: boolean = false) {
    const category = archived ? NEWS_CATEGORY_ARCHIVED : NEWS_CATEGORY;
    const wrapper = this.searchWrappers[category];
    runInAction(() => {
      wrapper.status = status;
    });
    if (status === FetchStatus.FETCHING) {
      this.resetSearchWrapper(category);
    }
    if (!search?.length) {
      runInAction(() => {
        this.searchWrappers[category].status = FetchStatus.FETCHED;
      });
      return;
    }

    try {
      const result = (
        await WebSearchApi.get('/news/search', {
          params: {
            query: search,
            lang: this.languageStore.currentLanguage,
            page: this.searchWrappers[category].currentPage,
            perPage: ITEMS_PER_PAGE,
            sortBy: this.searchWrappers[NEWS_CATEGORY].sortOrder,
            archived,
          },
        })
      ).data;
      this.setWrapperResults(category, result);
    } catch (e) {
      runInAction(() => {
        this.searchWrappers[category].status = FetchStatus.ERROR;
        this.resetSearchWrapper(category);
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      });
    }
  }

  public async fetchMoreNewsResults(archived: boolean) {
    const wrapper = archived ? this.searchWrappers[NEWS_CATEGORY_ARCHIVED] : this.searchWrappers[NEWS_CATEGORY];
    runInAction(() => {
      wrapper.currentPage += 1;
    });
    return this.fetchNewsResults(this.query, FetchStatus.FETCHING_MORE, archived);
  }

  public async fetchDealerSearchSuggestions() {
    try {
      runInAction(() => {
        this.dealerSearchSuggestionsWrapper.status = FetchStatus.FETCHING;
      });
      const response = await WebSearchApi.get('/dealer/search-history', {
        withCredentials: true,
      });
      this.setDealerSearchSuggestions(response.data);
    } catch (e) {
      runInAction(() => {
        ErrorUtil.pushErrorToTeliaRumAndLog(e);
        this.dealerSearchSuggestionsWrapper.total = 0;
        this.dealerSearchSuggestionsWrapper.results = [];
        this.dealerSearchSuggestionsWrapper.status = FetchStatus.ERROR;
      });
    }
  }

  private setDealerSearchSuggestions = (suggestions: string[]) => {
    const currentLocation = this.routeStore.pathname;
    runInAction(() => {
      this.dealerSearchSuggestionsWrapper.status = FetchStatus.FETCHED;
      this.dealerSearchSuggestionsWrapper.total = suggestions.length;
      this.dealerSearchSuggestionsWrapper.results = [
        {
          key: 'search.dealerSearchHistory',
          items: suggestions.map((suggestion) => ({
            label: suggestion,
            url: currentLocation + createParamsString({ query: suggestion }),
          })),
        },
      ];
    });
  };

  public fetchSuggestions(search: string, onlyShopSuggestions: boolean): Promise<void> {
    const trimmedSearch = search?.trim();
    runInAction(() => {
      this.lastSearchedQuery = search;
    });
    if (trimmedSearch.length < 3) {
      runInAction(() => {
        this.suggestionsWrapper.results = [];
      });
      return;
    }
    runInAction(() => {
      this.suggestionsWrapper.status = FetchStatus.FETCHING;
    });
    const language = this.languageStore.currentLanguage;
    const clientType = this.getClientType();
    try {
      return WebSearchApi.get(`/${onlyShopSuggestions ? 'shop' : 'common'}/v2/suggestions`, {
        params: { query: trimmedSearch, lang: language, clientType },
        cancelToken: this.suggestionCancel.token,
      })
        .then((response) => {
          runInAction(() => {
            this.suggestionsWrapper.results = this.getSuggestions(response.data);
            this.suggestionsWrapper.queryIds = {
              shop: response.data.shopResult?.queryId,
              web: response.data.webResult?.queryId,
              oa: response.data.oaResult?.queryId,
            };
            this.suggestionsWrapper.status = FetchStatus.FETCHED;
          });
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            this.resetSuggestionRequestCancelToken();
            return;
          }
          throw err;
        });
    } catch (e) {
      runInAction(() => {
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
        this.suggestionsWrapper.status = FetchStatus.ERROR;
      });
    }
  }

  private getSuggestions = (searchSuggestionResults: SearchSuggestionResults): SuggestionGroup[] => {
    const suggestions: SuggestionGroup[] = [];
    this.addSuggestionIfExists(suggestions, this.getShopResults(searchSuggestionResults));
    this.addSuggestionIfExists(suggestions, this.getSuggestionResults(searchSuggestionResults, false));
    this.addSuggestionIfExists(suggestions, this.getSuggestionResults(searchSuggestionResults, true));
    suggestions.forEach((result: { items?: { label?: string }[] }) => (result.items = result.items?.filter((item) => !!item.label) || []));
    return suggestions;
  };

  private addSuggestionIfExists = (suggestions: SuggestionGroup[], suggestionsResults: SuggestionGroup) => {
    if (suggestionsResults?.items?.length) {
      suggestions.push(suggestionsResults);
    }
  };

  private getShopResults = (searchSuggestionResults: SearchSuggestionResults) => {
    if (searchSuggestionResults.shopResult?.results?.length) {
      return {
        key: 'search.products',
        items: this.mapShopResults(searchSuggestionResults.shopResult.results),
      };
    }
    return null;
  };

  private getSuggestionResults = (searchSuggestionResults: SearchSuggestionResults, isWebResult: boolean) => {
    const result = isWebResult ? searchSuggestionResults.webResult : searchSuggestionResults.oaResult;
    if (result?.results?.length) {
      return {
        key: isWebResult ? 'search.content' : 'header.search.onlineHelp',
        items: this.mapContentResults(result.results),
      };
    }
    return null;
  };

  private mapShopResults = (results: ShopSearchSuggestion[]) => {
    return results.map((result: ShopSearchSuggestion) => {
      return {
        label: result.displayName[this.languageStore.currentLanguage],
        url: Config.teliaShopUrl + result.url,
        image: {
          srcset: cloudinaryUrlWithSize('22', result.pictures?.length && result.pictures[0], false),
          width: 22,
          alt: result.displayName[this.languageStore.currentLanguage],
        },
      };
    });
  };

  private mapContentResults = (results: SearchSuggestion[]) => {
    return results.map((result: SearchSuggestion) => ({
      url: result.url,
      label: result.title,
    }));
  };

  @action
  public switchDealerNewsPage(archive: boolean) {
    const wrapper = archive ? this.searchWrappers[EMPLOYEE_CATEGORY_ARCHIVED] : this.searchWrappers[EMPLOYEE_CATEGORY];
    wrapper.currentPage += 1;
    this.fetchDealerSearchResults(this.query, FetchStatus.FETCHING_MORE, archive);
  }

  public async sortDealerNews(sortOrder: DealerNewsSortOrder) {
    runInAction(() => {
      this.searchWrappers[EMPLOYEE_CATEGORY].sortOrder = sortOrder;
      this.searchWrappers[EMPLOYEE_CATEGORY].currentPage = 0;
      this.searchWrappers[EMPLOYEE_CATEGORY].segmentedResults = [];
      this.searchWrappers[EMPLOYEE_CATEGORY].segmentedPage = 0;
      this.searchWrappers[EMPLOYEE_CATEGORY_ARCHIVED].currentPage = 0;
    });
    const dealerSortPromises = [
      this.fetchDealerSearchResults(this.query, FetchStatus.REFETCHING),
      this.fetchDealerSearchResults(this.query, FetchStatus.REFETCHING, true),
    ];
    await Promise.all(dealerSortPromises);
  }

  public async sortNews(sortOrder: NewsSortOrder) {
    runInAction(() => {
      this.searchWrappers[NEWS_CATEGORY].sortOrder = sortOrder;
      this.searchWrappers[NEWS_CATEGORY].currentPage = 0;
      this.searchWrappers[NEWS_CATEGORY_ARCHIVED].currentPage = 0;
    });
    const newsSortPromises = [this.fetchNewsResults(this.query, FetchStatus.FETCHING), this.fetchNewsResults(this.query, FetchStatus.FETCHING, true)];
    await Promise.all(newsSortPromises);
  }

  public getEShopProductSortOrder() {
    let sortOrder = this.routeStore.decodedParams.sort;
    sortOrder = sortOrder && sortOrder.toUpperCase();
    return ProductsSortOrder[sortOrder as ProductsSortOrder] || ProductsSortOrder.RELEVANCE;
  }

  public postSearchResultClickEvent = (event: SearchEvent) => {
    try {
      WebSearchApi.post('/common/event', event);
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  };

  public getClientType = () => {
    if (this.ssoStore.customerType) {
      return this.ssoStore.customerType;
    }
    return this.segmentStore.currentSegment === SEGMENT_PRIVATE ? CUSTOMER_TYPE_PRIVATE : CUSTOMER_TYPE_BUSINESS;
  };
}

export default SearchStore;
